/**
 * Queue List Page (Presentation)
 *
 * Special listing page built on the shared DataTable with drag-and-drop,
 * socket.io real-time updates, and custom pagination following the queue
 * documentation.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import type { TableRowProps } from '@mui/material/TableRow';

import { DataTable, type ColumnDef, type SelectionConfig } from '@/shared/components/data-table/DataTable';
import { SearchInput } from '@/shared/components/data-table/SearchInput';
import { Pagination } from '@/shared/components/data-table/Pagination';
import { ColumnSelector, type ColumnOption } from '@/shared/components/data-table/ColumnSelector';
import { ConfirmModal } from '@/shared/components/modals/ConfirmModal';
import { useToast } from '@/shared/components/toasts';
import { queueListColumnCache } from '@/shared/services/columnVisibilityCacheService';
import { useSocketIOContext } from '@/shared/services/socketio';
import { SocketIONamespace } from '@/shared/services/socketio/types';
import { CallStatusIndicator } from '../components/CallStatusIndicator';
import { useQueueList, useMoveQueue, useDeleteQueues } from '../../application';
import { v2CallResponseToQueueModel } from '../../application/mappers/v2CallResponseToQueueModel';
import type { QueueModel, CallQueuePaginatedRequest, CallQueueDeleteRequest } from '../../domain/types';
import type { V2CallResponse, CallStatusEventData } from '@/shared/services/socketio/types';

// ============================================================================
// Default Request
// ============================================================================

const defaultRequest: CallQueuePaginatedRequest = {
  page: 1,
  per_page: 10,
  include_ended: false,
  all_call_queues: false,
};

/**
 * Stable empty list — keeps the row memoisation from re-running on every render.
 */
const EMPTY_QUEUES: QueueModel[] = [];

// ============================================================================
// Column Configuration
// ============================================================================

interface QueueColumnDef {
  key: string;
  label: string;
  isVisibleDefault: boolean;
  canShow: boolean;
  render: (queue: QueueModel) => ReactNode;
}

const QUEUE_COLUMNS: QueueColumnDef[] = [
  { key: 'order', label: 'Order', isVisibleDefault: true, canShow: true, render: q => q.order },
  { key: 'name', label: 'Name', isVisibleDefault: false, canShow: true, render: q => q.name },
  { key: 'callee', label: 'Callee', isVisibleDefault: true, canShow: true, render: q => q.callee },
  { key: 'status', label: 'Status', isVisibleDefault: true, canShow: true, render: q => <CallStatusIndicator status={q.status} size="small" /> },
  { key: 'tier', label: 'Tier', isVisibleDefault: true, canShow: true, render: q => q.tier },
  { key: 'amount_due', label: 'Amount Due', isVisibleDefault: true, canShow: true, render: q => q.amount_due || '-' },
  { key: 'dpd', label: 'DPD', isVisibleDefault: true, canShow: true, render: q => q.dpd || '-' },
];

// ============================================================================
// Drag Handle
// ============================================================================

type SortableResult = ReturnType<typeof useSortable>;

interface QueueRowContextValue {
  attributes: SortableResult['attributes'];
  listeners: SortableResult['listeners'];
  isDragging: boolean;
}

/**
 * Context carrying the dnd-kit sortable wiring down to the drag handle cell.
 */
const QueueRowContext = createContext<QueueRowContextValue | null>(null);

/**
 * Drag handle rendered inside the drag column of a sortable row.
 */
function QueueDragHandle() {
  const context = useContext(QueueRowContext);

  if (!context) {
    return null;
  }

  const { attributes, listeners, isDragging } = context;

  return (
    <IconButton size="small" aria-label="Drag to reorder" {...attributes} {...listeners} sx={{ cursor: isDragging ? 'grabbing' : 'grab' }} onClick={e => e.stopPropagation()}>
      <DragIndicatorIcon fontSize="small" />
    </IconButton>
  );
}

// ============================================================================
// Row Components
// ============================================================================

interface QueueRowProps extends TableRowProps {
  children?: ReactNode;
}

/**
 * Active (in-call) queue row — highlighted and never draggable.
 */
function ActiveQueueRow({ children, sx, ...props }: QueueRowProps) {
  return (
    <TableRow {...props} sx={[...(Array.isArray(sx) ? sx : [sx]), { cursor: 'pointer', bgcolor: 'action.selected' }]}>
      {children}
    </TableRow>
  );
}

/**
 * Non-active queue row — draggable via dnd-kit.
 */
function SortableQueueRow({ row, children, ...props }: QueueRowProps & { row: QueueModel }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id.toString(),
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const contextValue = useMemo<QueueRowContextValue>(
    () => ({
      attributes,
      listeners,
      isDragging,
    }),
    [attributes, listeners, isDragging]
  );

  return (
    <TableRow ref={setNodeRef} style={style} {...props}>
      <QueueRowContext.Provider value={contextValue}>{children}</QueueRowContext.Provider>
    </TableRow>
  );
}

/**
 * Row renderer handed to the DataTable — picks the active or sortable variant.
 */
function QueueTableRow({ row, children, ...props }: QueueRowProps & { row: QueueModel }) {
  const isActive = row.status.toLowerCase() === 'active';

  if (isActive) {
    return <ActiveQueueRow {...props}>{children}</ActiveQueueRow>;
  }

  return (
    <SortableQueueRow row={row} {...props}>
      {children}
    </SortableQueueRow>
  );
}

// ============================================================================
// Drag Column
// ============================================================================

const DRAG_COLUMN_KEY = '__drag__';

const DRAG_COLUMN: ColumnDef<QueueModel> = {
  key: DRAG_COLUMN_KEY,
  label: 'Drag',
  minWidth: 48,
  maxWidth: 48,
  render: (_value, queue) => (queue.status.toLowerCase() === 'active' ? null : <QueueDragHandle />),
};

// ============================================================================
// Main Component
// ============================================================================

export function QueueListPage() {
  const navigate = useNavigate();

  // State
  const [request, setRequest] = useState<CallQueuePaginatedRequest>(defaultRequest);
  const [searchText, setSearchText] = useState('');
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [isReordering, setIsReordering] = useState(false);
  const [localQueues, setLocalQueues] = useState<QueueModel[]>([]);
  const isReorderingRef = useRef(false);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set());
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);

  // Initialize column visibility from cache
  useEffect(() => {
    const allColumnKeys = new Set(QUEUE_COLUMNS.map(col => col.key));

    const cached = queueListColumnCache.getVisibleColumns();
    if (cached) {
      const validCached = new Set<string>();
      for (const key of cached) {
        if (allColumnKeys.has(key)) validCached.add(key);
      }
      setVisibleColumns(validCached.size > 0 ? validCached : allColumnKeys);
    } else {
      setVisibleColumns(allColumnKeys);
      queueListColumnCache.saveVisibleColumns(allColumnKeys);
    }

    const cachedOrder = queueListColumnCache.getColumnOrder();
    const defaultKeys = QUEUE_COLUMNS.map(col => col.key);
    const validCachedOrder = (cachedOrder ?? []).filter(key => allColumnKeys.has(key));
    const missingKeys = defaultKeys.filter(key => !validCachedOrder.includes(key));
    const nextOrder = [...validCachedOrder, ...missingKeys];
    setColumnOrder(nextOrder);

    if (!cachedOrder || cachedOrder.length === 0) {
      queueListColumnCache.saveColumnOrder(nextOrder);
    }
  }, []);

  // Column options for the ColumnSelector dialog
  const columnOptions: ColumnOption[] = useMemo(() => QUEUE_COLUMNS.map(col => ({ key: col.key, label: col.label, canShow: col.canShow, isVisibleDefault: col.isVisibleDefault })), []);

  // Filtered & ordered columns based on visibility and order preferences
  const visibleOrderedColumns: QueueColumnDef[] = useMemo(() => {
    const columnsByKey = new Map(QUEUE_COLUMNS.map(col => [col.key, col]));
    const orderedKeys = columnOrder.length > 0 ? columnOrder : QUEUE_COLUMNS.map(col => col.key);
    return orderedKeys.map(key => columnsByKey.get(key)).filter((col): col is QueueColumnDef => !!col && visibleColumns.has(col.key));
  }, [visibleColumns, columnOrder]);

  // DataTable columns — the drag column always comes first
  const tableColumns: ColumnDef<QueueModel>[] = useMemo(() => {
    const dataColumns: ColumnDef<QueueModel>[] = visibleOrderedColumns.map(col => ({
      key: col.key,
      label: col.label,
      render: (_value: unknown, queue: QueueModel) => col.render(queue),
    }));

    return [DRAG_COLUMN, ...dataColumns];
  }, [visibleOrderedColumns]);

  // Handlers for column selector
  const handleColumnsChange = useCallback((newVisibleColumns: Set<string>) => {
    setVisibleColumns(newVisibleColumns);
    queueListColumnCache.saveVisibleColumns(newVisibleColumns);
  }, []);

  const handleColumnOrderChange = useCallback((newOrder: string[]) => {
    setColumnOrder(newOrder);
    queueListColumnCache.saveColumnOrder(newOrder);
  }, []);

  // Reordering flag ref for socket updates
  useEffect(() => {
    isReorderingRef.current = isReordering;
  }, [isReordering]);

  // React Query
  const { data: queueResponse, isLoading, error, refetch } = useQueueList(request);
  const moveQueueMutation = useMoveQueue();
  const deleteQueueMutation = useDeleteQueues();
  const toast = useToast();

  // Socket.io
  const { subscribe, isConnected } = useSocketIOContext();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Update local queues when response changes
  useEffect(() => {
    if (queueResponse?.queues) {
      // Sort by order (ascending)
      const sorted = [...queueResponse.queues].sort((a, b) => a.order - b.order);
      setLocalQueues(sorted);
    }
  }, [queueResponse]);

  // Fall back to the query data while the local queue state is still syncing,
  // so the table never renders an empty state for a frame.
  const responseQueues = queueResponse?.queues;
  const rawQueues = localQueues.length > 0 ? localQueues : (responseQueues ?? EMPTY_QUEUES);

  // Rows rendered in the table — active calls stay pinned at the top.
  const displayQueues = useMemo(() => {
    const active: QueueModel[] = [];
    const nonActive: QueueModel[] = [];

    rawQueues.forEach(queue => {
      if (queue.status.toLowerCase() === 'active') {
        active.push(queue);
      } else {
        nonActive.push(queue);
      }
    });

    return [...active, ...nonActive];
  }, [rawQueues]);

  // Sortable item IDs (only non-active)
  const sortableIds = useMemo(() => displayQueues.filter(q => q.status.toLowerCase() !== 'active').map(q => q.id.toString()), [displayQueues]);

  // Socket.IO subscription for v2 call events (created + status)
  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = subscribe(event => {
      // Only handle /call namespace events
      if (event.namespace !== (SocketIONamespace.Call as string)) return;
      // Skip updates during reordering
      if (isReorderingRef.current) return;

      // created — new call added (queue or incoming)
      if (event.event === 'created') {
        const mapped = v2CallResponseToQueueModel(event.data as V2CallResponse);
        setLocalQueues(prev => {
          const idx = prev.findIndex(q => q.id === mapped.id);
          if (idx !== -1) {
            const next = [...prev];
            next[idx] = mapped;
            return next.sort((a, b) => a.order - b.order);
          }
          return [...prev, mapped].sort((a, b) => a.order - b.order);
        });
      }

      // status — call status changed
      if (event.event === 'status') {
        const data = event.data as CallStatusEventData;
        const shouldRemove = data.status === 'cancelled' || data.status === 'ended' || data.status === 'closed' || data.status === 'forwarded' || data.status === 'follow_up';

        setLocalQueues(prev => {
          const idx = prev.findIndex(q => q.id === data.call_id);
          if (idx === -1) return prev;
          if (shouldRemove) {
            const next = [...prev];
            next.splice(idx, 1);
            setTimeout(() => {
              void refetch();
            }, 1500);
            return next;
          }
          const next = [...prev];
          next[idx] = { ...next[idx], status: data.status as QueueModel['status'] };
          return next.sort((a, b) => a.order - b.order);
        });
      }
    });

    return unsubscribe;
  }, [subscribe, isConnected, refetch]);

  // Handle search change
  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
    setRequest(prev => {
      const newRequest: CallQueuePaginatedRequest = {
        ...prev,
        page: 1,
      };
      if (value) {
        newRequest.search_text = value;
      } else {
        delete newRequest.search_text;
      }
      return newRequest;
    });
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setRequest(prev => ({
      ...prev,
      page,
    }));
  }, []);

  // Handle per page change
  const handlePerPageChange = useCallback((perPage: number) => {
    setRequest(prev => ({
      ...prev,
      page: 1,
      per_page: perPage,
    }));
  }, []);

  // Calculate target order for reordering
  const calculateTargetOrder = useCallback((newIndex: number, queueList: QueueModel[]): number => {
    const activeCall = queueList.find(q => q.status.toLowerCase() === 'active');

    if (activeCall) {
      const activeCallOrder = activeCall.order;
      if (newIndex === 0) {
        // Moving to first position = position after active call
        return activeCallOrder + 1;
      } else {
        // Moving to other positions
        return newIndex + activeCallOrder + 1;
      }
    } else {
      // No active call, simple 1-based ordering
      return newIndex + 1;
    }
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = displayQueues.findIndex(q => q.id.toString() === active.id);
      const newIndex = displayQueues.findIndex(q => q.id.toString() === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const item = displayQueues[oldIndex];
      const isActive = item.status.toLowerCase() === 'active';

      // Prevent reordering if already in progress or item is active
      if (isReordering || isActive) {
        return;
      }

      // Adjust index if moving down
      let adjustedNewIndex = newIndex;
      if (newIndex > oldIndex) {
        adjustedNewIndex -= 1;
      }

      const targetOrder = calculateTargetOrder(adjustedNewIndex, displayQueues);

      // Optimistic UI update
      setIsReordering(true);
      const updatedList = arrayMove(displayQueues, oldIndex, adjustedNewIndex);
      setLocalQueues(updatedList);

      // Send reorder request
      moveQueueMutation.mutate(
        {
          queue_and_order: [
            {
              queue_id: item.id,
              target_order: targetOrder,
            },
          ],
          paginated: request,
        },
        {
          onSuccess: () => {
            // Reset reordering flag after successful move
            setTimeout(() => {
              setIsReordering(false);
            }, 2000);
          },
          onError: () => {
            // On error, reset flags and reload data
            setIsReordering(false);
            void refetch();
          },
        }
      );
    },
    [displayQueues, isReordering, calculateTargetOrder, moveQueueMutation, request, refetch]
  );

  // Handle row selection
  const handleSelectRow = useCallback((id: number, selected: boolean) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, []);

  // Handle select all
  const handleSelectAll = useCallback(
    (selected: boolean) => {
      if (selected) {
        const nonActiveIds = displayQueues.filter(q => q.status.toLowerCase() !== 'active').map(q => q.id);
        setSelectedRows(new Set(nonActiveIds));
      } else {
        setSelectedRows(new Set());
      }
    },
    [displayQueues]
  );

  // Handle move to top
  const handleMoveToTop = useCallback(() => {
    const idsToMove = Array.from(selectedRows);
    if (idsToMove.length === 0) {
      return;
    }

    moveQueueMutation.mutate(
      {
        queue_and_order: idsToMove.map(id => ({
          queue_id: id,
          target_order: 1, // Move to top (order 1)
        })),
        paginated: request,
      },
      {
        onSuccess: () => {
          setSelectedRows(new Set());
          void refetch();
        },
      }
    );
  }, [selectedRows, moveQueueMutation, request, refetch]);

  // Handle bulk delete
  const handleBulkDelete = useCallback(() => {
    setConfirmDeleteOpen(true);
  }, []);

  const handleConfirmBulkDelete = useCallback(async () => {
    const requests: CallQueueDeleteRequest[] = Array.from(selectedRows).map(id => ({
      id,
      status: 'cancelled',
    }));

    try {
      await deleteQueueMutation.mutateAsync(requests);
      setSelectedRows(new Set());
      toast.success(`${String(requests.length)} item(s) deleted`);
    } catch {
      toast.error('Failed to delete queue items');
    } finally {
      setConfirmDeleteOpen(false);
    }
  }, [selectedRows, deleteQueueMutation, toast]);

  // Handle row click → navigate to case detail
  const handleRowClick = useCallback(
    (queue: QueueModel) => {
      if (!queue.case_id) return;
      navigate(`/case-detail/${String(queue.case_id)}`);
    },
    [navigate]
  );

  // Check if all non-active items are selected
  const allSelected = useMemo(() => {
    const selectable = displayQueues.filter(q => q.status.toLowerCase() !== 'active');
    if (selectable.length === 0) return false;
    return selectable.every(q => selectedRows.has(q.id));
  }, [displayQueues, selectedRows]);

  const initialLoading = isLoading && !queueResponse;

  const total = queueResponse?.total ?? 0;
  const currentPage = queueResponse?.page ?? 1;
  const perPage = request.per_page;

  const selection: SelectionConfig<number, QueueModel> = {
    enabled: true,
    getRowId: queue => queue.id,
    isRowSelectable: queue => queue.status.toLowerCase() !== 'active',
    selectedIds: selectedRows,
    isAllSelected: allSelected,
    onToggleAll: () => handleSelectAll(!allSelected),
    onToggleOne: id => handleSelectRow(id, !selectedRows.has(id)),
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          Call Queue
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Tooltip title="Search by case ID, loan ID, name, NRIC, address, contact, tag, agent…" disableFocusListener placement="bottom-start">
            <span>
              <SearchInput value={searchText} onChange={handleSearchChange} placeholder="Search queue…" sx={{ minWidth: 250 }} />
            </span>
          </Tooltip>
          <Tooltip title="Select Columns">
            <IconButton aria-label="Select Columns" onClick={() => setColumnSelectorOpen(true)} color="primary" sx={{ border: 1, borderColor: 'divider' }}>
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Bulk Actions Bar */}
      {selectedRows.size > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: theme => (theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.1)),
            borderRadius: 1,
            border: 1,
            borderColor: theme => (theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.primary.main, 0.2)),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            {String(selectedRows.size)} item(s) selected
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Move selected to top">
              <IconButton onClick={handleMoveToTop} color="primary" disabled={moveQueueMutation.isPending} size="small" aria-label="Move selected to top">
                <ArrowUpwardIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete selected">
              <IconButton onClick={handleBulkDelete} color="error" disabled={deleteQueueMutation.isPending} size="small" aria-label="Delete selected">
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Pagination */}
      <Box sx={{ mb: 3 }}>
        <Pagination totalItems={total} currentPage={currentPage} perPage={perPage} onPageChange={handlePageChange} onPerPageChange={handlePerPageChange} disabled={isLoading} />
      </Box>

      {/* Table */}
      {error ? (
        <Box sx={{ p: 4 }}>
          <Typography color="error">Error loading queue: {error.message}</Typography>
        </Box>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            <DataTable<QueueModel, number>
              columns={tableColumns}
              rows={displayQueues}
              rowComponent={QueueTableRow}
              onRowClick={handleRowClick}
              selection={selection}
              loading={initialLoading}
              emptyMessage="No queue items available"
            />
          </SortableContext>
        </DndContext>
      )}

      {/* Socket connection indicator */}
      {!isConnected && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
          <Typography variant="body2" color="warning.dark">
            Socket disconnected. Real-time updates unavailable.
          </Typography>
        </Box>
      )}

      {/* Column Selector Dialog */}
      <ColumnSelector
        columns={columnOptions}
        visibleColumns={visibleColumns}
        onColumnsChange={handleColumnsChange}
        columnOrder={columnOrder}
        onColumnOrderChange={handleColumnOrderChange}
        open={columnSelectorOpen}
        onClose={() => setColumnSelectorOpen(false)}
        title="Select Columns to Display"
      />

      <ConfirmModal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmBulkDelete}
        title="Delete Queue Items"
        message={`Are you sure you want to delete ${String(selectedRows.size)} item(s) from the queue?`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteQueueMutation.isPending}
      />
    </Box>
  );
}
